import MiniSearch, { Query } from 'minisearch';
import Contact from '@/grpc-api/model/contact';

interface SearchContact
{
  id: string;
  name?: string;
  companyName?: string;
  phone?: string;
  email?: string;
}

type ContactField = 'name' | 'companyName' | 'phone' | 'email';

const ContactFields: ContactField[] = ['name', 'companyName', 'phone', 'email'];

const TOKEN_SEPARATOR = /[\s-]+/;

/**
 * Tokenizer matching `elasticlunr.tokenizer`'s default behavior: trim +
 * lowercase + split on whitespace / hyphens. No stemming or stop-word
 * filtering (the original contact index used an empty pipeline).
 */
function tokenize(text: string | null | undefined): string[] {
  if (text === null || text === undefined) return [];
  return String(text).trim().toLowerCase().split(TOKEN_SEPARATOR)
    .filter((t) => t !== '');
}

function toSearchContact(c: Contact): SearchContact {
  return {
    id: String(c.id),
    name: c.name ?? undefined,
    companyName: c.companyName ?? undefined,
    phone: c.phone ?? undefined,
    email: c.email ?? undefined,
  };
}

function searchCreate(): MiniSearch<SearchContact> {
  return new MiniSearch<SearchContact>({
    idField: 'id',
    fields: ContactFields as unknown as string[],
    tokenize: (text: string) => tokenize(text),
    processTerm: (term: string) => term,
    searchOptions: {
      prefix: true,
      combineWith: 'AND',
    },
  });
}

let cindex = searchCreate();

export function searchContactAdd(contact: Contact[]) {
  contact.forEach((c) => {
    const doc = toSearchContact(c);
    if (cindex.has(doc.id)) cindex.replace(doc);
    else cindex.add(doc);
  });
}

export function searchContactRemove(contact: Contact[]) {
  contact.forEach((c) => {
    const id = String(c.id);
    if (cindex.has(id)) cindex.discard(id);
  });
}

export function searchContactReset() {
  cindex = searchCreate();
}

export function searchForContacts(query: string, contactsById: Map<number, Contact>) {
  if (query.trim().length < 3) return [];

  // Emulate elasticlunr's per-field AND (all terms must appear in the same
  // field) OR across fields, with prefix expansion (equivalent to
  // `expand: true`).
  const combined: Query = {
    combineWith: 'OR',
    queries: ContactFields.map((field) => ({
      combineWith: 'AND',
      queries: [query],
      fields: [field],
      prefix: true,
    } as unknown as Query)),
  } as unknown as Query;

  const res = cindex.search(combined);

  console.log('searchForContacts: ', query, res);
  return res.flatMap((c) => contactsById.get(Number(c.id)) ?? []);
}
